import { Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { SessionService } from '@core/services/session.service';
import { ToastController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { ROUTE_STATUS } from '../../../routes/models/RouteStatus';
export interface Pagination {
  sort: { field: string, reverse?: boolean };
  page: number;
  pageSize: number;
  totalElements?: number;
}
export interface FiltersTypes {
  'date'?: { data: [number, number] },
  'driver'?: { data: { id: number, name: string }[] },
  'dateIso'?: { data: string },
  'status'?: { data: string[] }
}
export interface StateSort {
  field: string;
  reverse?: boolean;
}
export interface StateFieldBetween {
  field: string;
  value1: string;
  value2: string;
}
export interface StatePaginatedRequest {
  fields: string[];
  sort: StateSort;
  page: number;
  pageSize: number;
  filters?: any;
  between?: StateFieldBetween[];
}
export interface StateResponse<T> {
  data: T[];
  total: number;
  state: StatePaginatedRequest;
}
const DefaultFilters: FiltersTypes = {
  date: { data: [new Date().setHours(15, 0, 0, 0), new Date().setHours(23, 59, 59)] },
  status: { data: [ROUTE_STATUS.PENDING, ROUTE_STATUS.STARTED, ROUTE_STATUS.FINISHED, ROUTE_STATUS.FINISHED_WITH_ERRORS] },
};
@Injectable({ providedIn: 'root' })
export abstract class BaseEntitiesService<T> {
  protected readonly _selected: BehaviorSubject<T> = new BehaviorSubject<T>(null);
  public readonly selected = this._selected.asObservable();
  protected readonly _filters: BehaviorSubject<FiltersTypes> = new BehaviorSubject<FiltersTypes>(DefaultFilters);
  public readonly filters = this._filters.asObservable();
  protected readonly _pagination: BehaviorSubject<Pagination> = new BehaviorSubject<Pagination>({ sort: { field: 'initDate', reverse: false }, page: 0, pageSize: 20, totalElements: 0 });
  public readonly pagination = this._pagination.asObservable();
  protected readonly _total: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly total = this._total.asObservable();
  protected readonly _elements = new BehaviorSubject<T[]>(null);
  readonly elements = this._elements.asObservable();
  protected constructor(protected _api: ApiService, protected _toast: ToastController, protected _session: SessionService) {
  }
  // Filters
  public setPagination(pagination: Pagination): void {
    this._pagination.next(pagination);
  }
  public setFilters(filters: FiltersTypes): void {
    this._filters.next(filters);
    this.updateApplied();
  }
  public getFiltersSnapshot(): FiltersTypes {
    return this._filters.getValue();
  }
  public getPaginationSnapshot(): Pagination {
    return this._pagination.getValue();
  }
  // Selected element
  public abstract selectElement(element: T): void;
  public abstract getSelectedElement(): T;
  // Repository
  public setElements(elements: T[]): void {
    this._elements.next(elements);
  }
  public getElements(): T[] {
    return this._elements.getValue();
  }
  public abstract add(element: T | T[]): Promise<T>;
  public abstract update(element: T): Promise<T | boolean>;
  public abstract remove(elements: T[]): Promise<void>;
  public loadElements(): Promise<T[]> {
    return null;
  }
  public resetFilters() {
    this._filters.next(DefaultFilters);
  }
  private updateApplied() {
    let total = 0;
    if (this._filters.getValue()) {
      total = Object.keys(this._filters.getValue()).reduce((a: number, b: string) => a + ((this._filters.getValue() as any)[b].data ? 1 : 0), 0);
    }
    this._total.next(total > 0 ? total : 0);
  }
}
